import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { menuBack, menuEnter, menuNavigate } from '~/modules/SoundManager';
import useKeyboard from '~/modules/hooks/useKeyboard';
import { HelpEntry } from '~/routes/KeyboardHelp/Context';
import useKeyboardHelp from './useKeyboardHelp';

/**
 * todo: Bug: for some reason elementList is duplicated eg.
 * ['a', 'b', 'c', 'a', 'b', 'c']
 *
 * But still works even with dynamic elements :shrugs:
 */
interface Options {
  enabled?: boolean;
  onBackspace?: () => void;
  backspaceHelp?: string | null;
  direction?: 'horizontal' | 'vertical';
  additionalHelp?: HelpEntry;
}

interface KeyboardAction {
  callback: () => void;
  label?: string;
  propName: string;
}

type InputMode = 'keyboard' | 'mouse';

const keyboardInputKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ', 'Tab'];

const getInitialInputMode = (): InputMode => {
  if (typeof document === 'undefined') return 'keyboard';
  return document.body.dataset.inputMode === 'mouse' ? 'mouse' : 'keyboard';
};

export default function useKeyboardNav(options: Options = {}, debug = false) {
  const { enabled = true, onBackspace, backspaceHelp = null, direction = 'vertical', additionalHelp = {} } = options;

  const [currentlySelected, setCurrentlySelected] = useState<string | null>(null);
  const [inputMode, setInputMode] = useState<InputMode>(getInitialInputMode);
  const elementList = useRef<string[]>([]);
  const newElementList = useRef<string[]>([]);
  const actions = useRef<Record<string, KeyboardAction>>({});

  const setNavigationInputMode = useCallback((mode: InputMode) => {
    if (typeof document !== 'undefined') {
      document.body.dataset.inputMode = mode;
    }
    setInputMode((current) => (current === mode ? current : mode));
  }, []);

  const currentlySelectedActionLabel = actions.current[currentlySelected!]?.label;
  const help = useMemo(
    () => ({
      [direction]: null,
      accept: currentlySelectedActionLabel ?? null,
      back: onBackspace ? backspaceHelp : undefined,
      ...additionalHelp,
    }),
    [currentlySelectedActionLabel, additionalHelp, backspaceHelp, direction, onBackspace],
  );
  useKeyboardHelp(help, enabled);

  const handleEnter = () => {
    if (!currentlySelected || !actions.current[currentlySelected]) return;
    setNavigationInputMode('keyboard');
    actions.current[currentlySelected]?.callback();
    menuEnter.play();
  };

  const handleBackspace = () => {
    if (onBackspace) {
      setNavigationInputMode('keyboard');
      menuBack.play();
      onBackspace();
    }
  };

  const handleNavigation = (direction: -1 | 1) => {
    if (!elementList.current.length) return;

    setNavigationInputMode('keyboard');
    const selectedIndex = currentlySelected ? elementList.current.indexOf(currentlySelected) : -1;
    const currentIndex = selectedIndex >= 0 ? selectedIndex : direction > 0 ? -1 : 0;
    const nextIndex = (elementList.current.length + currentIndex + direction) % elementList.current.length;
    menuNavigate.play();

    setCurrentlySelected(elementList.current[nextIndex] ?? null);
  };

  useKeyboard(
    {
      [direction === 'vertical' ? 'up' : 'left']: () => handleNavigation(-1),
      [direction === 'vertical' ? 'down' : 'right']: () => handleNavigation(1),
      accept: handleEnter,
      back: handleBackspace,
    },
    enabled,
    [currentlySelected, elementList.current],
  );

  useEffect(() => {
    if (!enabled) return;

    const handleKeyboardInput = (e: KeyboardEvent) => {
      if (keyboardInputKeys.includes(e.key)) {
        setNavigationInputMode('keyboard');
      }
    };

    const handlePointerInput = () => {
      setNavigationInputMode('mouse');
    };

    window.addEventListener('keydown', handleKeyboardInput, { passive: true });
    window.addEventListener('pointermove', handlePointerInput, { passive: true });
    window.addEventListener('pointerdown', handlePointerInput, { passive: true });

    return () => {
      window.removeEventListener('keydown', handleKeyboardInput);
      window.removeEventListener('pointermove', handlePointerInput);
      window.removeEventListener('pointerdown', handlePointerInput);
    };
  }, [enabled, setNavigationInputMode]);

  let defaultSelection = '';

  const register = (
    name: string,
    onActive: (e?: Event) => void,
    help?: string,
    isDefault = false,
    { propName = 'onClick', disabled = false } = {},
  ) => {
    if (disabled) {
      return { disabled, focused: false };
    }
    if (!newElementList.current.includes(name)) {
      newElementList.current.push(name);
    }

    if (onActive) actions.current[name] = { callback: onActive, label: help, propName };

    if (isDefault) {
      defaultSelection = name;
    }

    const focused = enabled && inputMode === 'keyboard' && currentlySelected === name;

    return {
      focused,
      [propName]: onActive,
      $keyboardNavigationChangeFocus: handleNavigation,
      'data-test': name,
      ...(enabled ? { 'data-focused': focused, 'data-e2e-focused': focused } : {}),
    };
  };

  useEffect(() => {
    if (inputMode !== 'keyboard' || !currentlySelected) return;

    document
      .querySelector(`[data-test="${currentlySelected}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }, [currentlySelected, inputMode]);

  useEffect(() => {
    const newElements = newElementList.current.filter((e) => !elementList.current.includes(e));
    debug && newElements.length && console.log('new elements', newElements);
    elementList.current = [...newElementList.current];
    newElementList.current.length = 0;

    if (!elementList.current.length) return;
    if (
      currentlySelected === null ||
      elementList.current.indexOf(currentlySelected) === -1 ||
      newElements.includes(defaultSelection)
    ) {
      setCurrentlySelected(defaultSelection || elementList.current[0]);
    }
  });

  const focusElement = (name: string, mode?: InputMode) => {
    if (elementList.current.includes(name)) {
      if (mode) {
        setNavigationInputMode(mode);
      }
      setCurrentlySelected(name);
    }
  };

  return {
    focusElement,
    focused: currentlySelected,
    register,
  };
}

export type RegisterFunc = ReturnType<typeof useKeyboardNav>['register'];
